<script setup lang="ts">
import { ref, computed } from 'vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import type { Tender } from '@/types/tender'
import { useAuthStore } from '@/stores/auth';

// Define props at the top level
const props = defineProps<{
  tender: Tender
}>()

const authStore = useAuthStore()
const userType = authStore.user?.userType

// Reactive state for showing full tender brief
const showFullTenderBrief = ref(false)

// Formatting functions
const formatNumber = (num?: number | null): string => {
  if (!num) return '₹0'
  if (num >= 10000000) return '₹' + (num / 10000000).toFixed(2) + ' Cr'
  if (num >= 100000) return '₹' + (num / 100000).toFixed(2) + ' L'
  if (num >= 1000) return '₹' + (num / 1000).toFixed(2) + ' K'
  return '₹' + num.toLocaleString('en-IN')
}

// Helper to check if a value exists
const hasValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim() !== ''
  if (typeof value === 'number') return !isNaN(value) && value > 0
  return true
}

// Helper to truncate text to 300 characters
const truncateText = (text: string, maxLength: number = 300): string => {
  if (!text || text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

// Check if tender brief needs truncation - use props.tender
const isTenderBriefTruncated = computed(() => {
  const brief = props.tender?.tenderBrief
  return brief && brief.length > 300
})

// Get truncated or full tender brief based on state
const displayTenderBrief = computed(() => {
  const brief = props.tender?.tenderBrief || ''
  if (!brief) return ''
  
  return showFullTenderBrief.value ? brief : truncateText(brief, 300)
})
</script>

<template>
  <CardComponent
    title="Tender Overview"
    :showHeaderBorder="true"
    :showHeaderPaddingBottom="true"
  >
    <template #body>
      <div class="grid grid-cols-1 gap-6">
        <!-- Basic Information -->
        <div class="space-y-4">
          <h3
            class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider"
          >
            Basic Information
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Organization</p>
              <p class="text-sm font-medium text-gray-800 dark:text-white">
                {{ tender?.organization || 'N/A' }}
              </p>
            </div>
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Location</p>
              <p class="text-sm font-medium text-gray-800 dark:text-white">
                {{ tender?.location || 'N/A' }}
              </p>
            </div>
            <div v-if="userType !== 'engineer'">
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Estimated Cost</p>
              <p class="text-sm font-medium text-gray-800 dark:text-white">
                {{ formatNumber(tender?.estimatedCost) }}
              </p>
            </div>
            <div v-if="hasValue(tender?.documentFees) && userType !== 'engineer'">
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Document Fees</p>
              <p class="text-sm font-medium text-gray-800 dark:text-white">
                {{ formatNumber(tender.documentFees) }}
              </p>
            </div>
            <div v-if="hasValue(tender?.emd) && userType !== 'engineer'">
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">EMD Amount</p>
              <p class="text-sm font-medium text-gray-800 dark:text-white">
                {{ formatNumber(tender.emd) }}
              </p>
            </div>
            <div v-if="hasValue(tender?.quantity)">
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Quantity / Scope</p>
              <p class="text-sm font-medium text-gray-800 dark:text-white">
                {{ tender.quantity }}
              </p>
            </div>
          </div>
        </div>
      
        <!-- Description & Criteria -->
        <div
          v-if="userType !== 'engineer'"
          class="space-y-6"
        >
          <div v-if="hasValue(tender?.tenderBrief)">
            <div class="flex items-center justify-between mb-3">
              <h3
                class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider"
              >
                Tender Brief
              </h3>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              {{ displayTenderBrief }} <button
                v-if="isTenderBriefTruncated"
                @click="showFullTenderBrief = !showFullTenderBrief"
                class="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors"
              >
                 {{ showFullTenderBrief ? 'Read less' : 'Read more' }}
              </button>
            </p>
          </div>

          <!-- Eligibility Criteria -->
          <div v-if="hasValue(tender?.eligibilityCriteria)">
            <h3
              class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3"
            >
              Eligibility Criteria
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              {{ tender.eligibilityCriteria }}
            </p>
          </div>

          <!-- Document -->
          <div v-if="hasValue(tender?.documentRequiredFromSeller)">
            <h3
              class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3"
            >
              Required Documents
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              {{ tender.documentRequiredFromSeller }}
            </p>
          </div>

          <!-- Checklist -->
          <div v-if="hasValue(tender?.checklist)">
            <h3
              class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3"
            >
              Checklist
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              {{ tender.checklist }}
            </p>
          </div>
        </div>
      </div>
    </template>
  </CardComponent>
</template>